import { Badge, Group, Table, Text } from "@mantine/core"
import { useDisclosure } from "@mantine/hooks"
import { IconEdit, IconTrash } from "@tabler/icons-react"
import { ActionIcon, CountryCell } from "@tfg/ui"
import { useState } from "react"
import { useTranslation } from "react-i18next"

import { SupplierResponses } from "@tfg/types/endpoints/supplier"

import { DeleteProductionSiteModal } from "~/pages/SupplierPortal/ProductionSites/components/DeleteProductionSiteModal.tsx"
import { EditProductionSiteDrawer } from "~/pages/SupplierPortal/ProductionSites/components/EditProductionSiteDrawer.tsx"
import { useAuth } from "~/store/auth-context.tsx"
import { COUNTRIES } from "~/utils/countries"

type Props = {
  productionSites: SupplierResponses.ProductionSite[]
}
export const ProductionSiteTable = ({ productionSites }: Props) => {
  const { t } = useTranslation(["supplier"])
  const { isAdmin } = useAuth()
  const [openedDelete, { open: openDelete, close: closeDelete }] =
    useDisclosure()

  const [editedProductionSite, setEditedProductionSite] = useState<
    SupplierResponses.ProductionSite | undefined
  >()
  const [deletedProductionSite, setDeletedProductionSite] =
    useState<SupplierResponses.ProductionSite>()

  return (
    <>
      <Table verticalSpacing="sm" stickyHeader stickyHeaderOffset={60}>
        <Table.Thead>
          <Table.Tr>
            {isAdmin && (
              <Table.Th>
                <Text c="violet">{t("productionSites.table.headers.id")}</Text>
              </Table.Th>
            )}
            {isAdmin && (
              <Table.Th>
                <Text c="violet">Deleted</Text>
              </Table.Th>
            )}
            <Table.Th>{t("productionSites.table.headers.name")}</Table.Th>
            <Table.Th>{t("productionSites.table.headers.address")}</Table.Th>
            <Table.Th>{t("productionSites.table.headers.country")}</Table.Th>
            <Table.Th>
              {t("productionSites.table.headers.certificates")}
            </Table.Th>
            <Table.Th>{t("productionSites.table.headers.actions")}</Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {productionSites?.map(
            (site) =>
              (site.deletedAt === null || isAdmin) && (
                <Table.Tr key={site.id}>
                  {isAdmin && (
                    <Table.Td>
                      <Text c="violet">{site.id}</Text>
                    </Table.Td>
                  )}
                  {isAdmin && (
                    <Table.Td>
                      {site.deletedAt ? (
                        <Badge bg="red" size="10" circle />
                      ) : null}
                    </Table.Td>
                  )}
                  <Table.Td style={{ maxWidth: "250px" }}>{site.name}</Table.Td>
                  <Table.Td>{site.address}</Table.Td>
                  <Table.Td>
                    <CountryCell
                      key={site.country}
                      countryIso3166={site.country}
                      countriesMapper={COUNTRIES}
                    />
                  </Table.Td>
                  <Table.Td>
                    {site.certificates.map((c) => (
                      <Badge key={c.id} size="md" c="gray">
                        {c.type}
                      </Badge>
                    ))}
                  </Table.Td>
                  <Table.Td style={{ minWidth: "240px" }}>
                    {site.deletedAt === null && (
                      <Group wrap="nowrap">
                        <ActionIcon
                          icon={<IconEdit />}
                          onClick={() => setEditedProductionSite(site)}
                        />
                        <ActionIcon
                          variant="secondary-critical"
                          color="red"
                          icon={<IconTrash />}
                          onClick={() => {
                            setDeletedProductionSite(site)
                            openDelete()
                          }}
                        />
                      </Group>
                    )}
                  </Table.Td>
                </Table.Tr>
              )
          )}
        </Table.Tbody>
      </Table>
      {editedProductionSite && (
        <EditProductionSiteDrawer
          productionSite={editedProductionSite}
          onClose={() => setEditedProductionSite(undefined)}
        />
      )}

      {deletedProductionSite && (
        <DeleteProductionSiteModal
          isOpen={openedDelete}
          onClose={() => closeDelete()}
          productionSite={deletedProductionSite}
        />
      )}
    </>
  )
}
